import React, { ChangeEvent } from 'react';
import { Grid, Theme, useMediaQuery } from '@mui/material';
import useNestedList from 'hooks/useNestedList';
import { useQueryParams } from 'hooks';
import { ParsedUrlQueryInput } from 'querystring';
import { getValue, idValueFormatter, nameValueParser } from 'components/filters/utils';

import FilterSelectHandler from 'components/filters/FilterSelectHandler';

export type ItemType = {
  id: number;
  parent?: number | null;
};

export interface NestedMultipleFilterSelectProps<T extends ItemType> {
  items: T[];
  disabled: boolean;
  name: string;
  placeholderFormatter: (item?: T) => string;
  labelFormatter: (item: T) => string;
}

const NestedMultipleFilterSelect = <T extends ItemType>(props: NestedMultipleFilterSelectProps<T>) => {
  const { items: allItems, name, placeholderFormatter, ...rest } = props;
  const [queryParams] = useQueryParams();
  const { itemsById, childItemsById, rootItems } = useNestedList<T>(allItems);
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));
  const value = nameValueParser(queryParams, name);
  const activeParentId = new Set();

  const valueTransformer = ({ target: { name, value } }: ChangeEvent<HTMLInputElement>, query: ParsedUrlQueryInput) => {
    if (Array.isArray(query[name]) && (query[name] as string[])?.length > value.length) {
      const id = (query[name] as string[]).find((x) => !value.includes(x));
      if (childItemsById[Number(id)]) {
        activeParentId.delete(Number(id));
        const newValue = (value as unknown as string[]).filter(
          (value) => !childItemsById[Number(id)].some((child) => child.id === Number(value)),
        );
        return { [name]: newValue };
      }
    }

    return { [name]: value };
  };

  const renderItem = (items: T[], item?: T) => {
    const clearValueParser = () => {
      if (value) {
        if (item && Array.isArray(value)) {
          const filterValue = value.filter((id) => !items.find((item) => item.id === Number(id)));
          return { [name]: filterValue };
        }
        if (!item) return { [name]: undefined };
      }
    };

    return (
      <Grid key={items[0]?.id} item container={isMobile} {...(isMobile && { direction: 'column' })}>
        <FilterSelectHandler
          multiple
          name={name}
          placeholder={placeholderFormatter(item)}
          {...rest}
          items={items}
          allItems={allItems}
          valueFormatter={idValueFormatter}
          valueTransformer={valueTransformer}
          valueParser={nameValueParser}
          clearValueParser={clearValueParser}
        />
      </Grid>
    );
  };

  return (
    <Grid container spacing={1}>
      {renderItem(rootItems)}
      {value &&
        (getValue(value, { multiple: true }) as string[]).map((valueId) => {
          const id = Number(valueId);
          const item = itemsById[id];
          if (item) {
            if (!item.parent && !childItemsById[item.id]) {
              return null;
            }

            if (activeParentId.has(item.parent) || activeParentId.has(item.id)) return null;

            if (!item.parent && !activeParentId.has(item.id) && childItemsById[item.id]) {
              activeParentId.add(item.id);
              return renderItem(childItemsById[item.id], item);
            }

            activeParentId.add(item.parent);
            return renderItem(childItemsById[item.parent], item);
          }

          return null;
        })}
    </Grid>
  );
};

export default NestedMultipleFilterSelect;
